import Model from 'src/app/common/model';
import FeedLogModel from './feedLog.model';
import ItemMasterModel from './itemMaster.model';
import TreatmentLogModel from './treatmentLog.model';
import UOMMasterModel from './uom.model';
import UOMConversionModel from './uomConversion.model';

/**
 * @model BatchTransactionsModel
 * @description
 * Represents the data structure for managing batch transactions within the AquaCloud application.
 * This model serves as a comprehensive Data Transfer Object (DTO) for handling grouped operations
 * related to feeding and treatments in aquaculture farming.
 *
 * **Purpose:**
 * The primary purpose of this model is to encapsulate all necessary data required to perform
 * or retrieve batch operations. Instead of logging a single feed event per pond, users often
 * perform "batch" entries where multiple ponds are fed simultaneously or a treatment is applied
 * across a section. This model aggregates those individual logs (`FeedLogs` or `TreatmentLogs`)
 * along with shared metadata like time, person, and location.
 *
 * **Key Responsibilities:**
 * 1. **Aggregation**: Groups multiple `FeedLogModel` or `TreatmentLogModel` entries under a single transaction header.
 * 2. **Contextualization**: Stores shared context such as `farm_site_id`, `section_id`, and `person_id` that applies to all logs in the batch.
 * 3. **Type Discrimination**: Uses `batch_type` to distinguish between Feed operations and Treatment operations.
 * 4. **Reference Management**: Handles links to inventory items (`itemMaster`) and unit of measure conversions (`UOMConversion`) required for stock deduction.
 *
 * **Data Flow:**
 * - **Input**: Populated by components (e.g., Batch Entry Forms) when a user submits a daily log.
 * - **Processing**: Passed to `BatchTransactionsService` which validates and splits the batch into individual database records or stores it as a transaction block.
 * - **Output**: Returned by API endpoints when querying historical logs for a specific date range (`fromDate`, `toDate`) or section.
 *
 * **User Roles & Permissions:**
 * - **Technicians/Farmers**: Create these models when logging daily activities via the mobile or web app.
 * - **Managers/Admins**: Retrieve these models to view reports, audit usage, or revert erroneous entries.
 *
 * **Features:**
 * - Supports both Feed and Treatment workflows.
 * - Includes support for reverting transactions via the `revert` flag.
 * - Handles complex unit conversions embedded within the transaction data.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class BatchTransactionsModel extends Model {
  /**
   * @type {Array<FeedLogModel>}
   * @description Collection of individual feed log entries associated with this batch.
   * Populated when `batch_type` is 'Feed'.
   * @optional
   */
  FeedLogs?: Array<FeedLogModel>;

  /**
   * @type {Array<ItemMasterModel>}
   * @description Reference details for the inventory items (feed or medicine) used in this transaction.
   * Used for displaying item names and codes without separate API calls.
   * @optional
   */
  itemMaster?: Array<ItemMasterModel>;

  /**
   * @type {Array<TreatmentLogModel>}
   * @description Collection of individual treatment log entries associated with this batch.
   * Populated when `batch_type` is 'Treatment'.
   * @optional
   */
  TreatmentLogs?: Array<TreatmentLogModel>;

  /**
   * @type {UOMMasterModel}
   * @description Primary Unit of Measure details for the batch quantity.
   * @optional
   */
  UOMMaster?: UOMMasterModel;

  /**
   * @type {Array<UOMMasterModel>}
   * @description List of available Units of Measure relevant to the items in this batch.
   * @optional
   */
  uom?: Array<UOMMasterModel>;

  /**
   * @type {Array<UOMConversionModel>}
   * @description Conversion factors used to normalize quantities (e.g., converting bags to kgs).
   * Critical for accurate inventory deduction.
   * @optional
   */
  UOMConversion?: Array<UOMConversionModel>;

  /**
   * @type {string}
   * @description The timestamp of when the transaction was recorded or when the event occurred.
   * Format: ISO 8601 string.
   * @optional
   */
  date_time?: string;

  /**
   * @type {string}
   * @description The specific time of day the feed or treatment was given.
   * Often separated from the date for UI display purposes.
   * @optional
   */
  given_time?: string;

  /**
   * @type {number}
   * @description ID of a related batch, used for linking corrections or follow-up transactions.
   * @optional
   */
  reference_batch_id?: number;

  /**
   * @type {boolean}
   * @description Flag indicating if this transaction is a reversal of a previous one.
   * Used for correcting data entry errors.
   * @optional
   */
  revert?: boolean;

  /**
   * @type {string}
   * @description The unique identifier of the business entity owning this data.
   * @optional
   */
  business_id?: string;

  /**
   * @type {string}
   * @description The name of the business entity.
   * @optional
   */
  business_name?: string;

  /**
   * @type {string}
   * @description The unique identifier of the farm site where the transaction took place.
   * @optional
   */
  farm_site_id?: string;

  /**
   * @type {string}
   * @description A unique alphanumeric code assigned to this specific batch operation.
   * @optional
   */
  batch_number?: string;

  /**
   * @type {'Feed' | 'Treatment'}
   * @description Discriminator field to identify the type of operation.
   * Determines whether `FeedLogs` or `TreatmentLogs` should be processed.
   * @optional
   */
  batch_type?: 'Feed' | 'Treatment';

  /**
   * @type {number}
   * @description The identifier for the specific section within the farm site.
   * @optional
   */
  section_id?: number;

  /**
   * @type {string}
   * @description The unique identifier of the person (user) who performed the action.
   * @optional
   */
  person_id?: string;

  /**
   * @type {boolean}
   * @description Status flag indicating if the record is active or soft-deleted.
   * @optional
   */
  isActive?: boolean;

  /**
   * @type {any}
   * @description Start date filter for querying a range of batch transactions.
   * Typically used in search/filter payloads, not in the stored record.
   * @optional
   */
  fromDate?: any;

  /**
   * @type {any}
   * @description End date filter for querying a range of batch transactions.
   * Typically used in search/filter payloads, not in the stored record.
   * @optional
   */
  toDate?: any

 /**
  * @constructor
  * @description
  * Initializes a new instance of the BatchTransactionsModel.
  * Sets up empty arrays for `FeedLogs` and `TreatmentLogs` to ensure they are ready for population.
  * Calls the parent `Model` constructor.
  */
 constructor() {
    super();
    this.FeedLogs = new Array();
    this.TreatmentLogs = new Array();
  }
}
